<template>
  <div>
    <h1 class="text-2xl font-semibold text-gray-900">Offrir un animal</h1>
    <!-- En-tête avec description -->
    <div class="mx-auto text-left mb-12">
      <div class="text-gray-600">
        <p>
          A l'occasion d'un anniversaire, pour Noël, lors de la fête des mères, des pères ou encore pour marquer le coup lors d'un événement, vous pouvez faire un cadeau qui a du sens !
        </p>
        <p>
          En leur « offrant » une poule, une brebis, ou bien plus encore, vous faites plaisir à bien plus qu'une personne ! Vous faites plaisir à vos proches, tout en aidant une famille rurale à développer une nouvelle activité génératrice de revenus.
        </p>
        <p>
          Par l'intermédiaire de Zebunet, financez des parrainages, au nom de vos proches !
        </p>
      </div>
    </div>
    <div class="mt-6">
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-zebu-orange border-b-transparent"></div>
      </div>
      <div v-else-if="error" class="text-center text-red-600">
        {{ error }}
      </div>
      <div v-else-if="!projects || projects.length === 0" class="text-center">
        <p class="text-gray-500">Aucun projet n'est disponible pour le moment.</p>
      </div>
      <div v-else class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="project in projects"
          :key="project.id"
          class="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300"
        >
          <div class="relative pb-64">
            <img
              :src="getProjectImage(project.cover)"
              :alt="project.name"
              class="absolute h-full w-full object-cover"
              @error="handleImageError"
            />
          </div>
          <div class="p-5">
            <h3 class="text-lg font-medium text-gray-900">{{ project.name }}</h3>
            <div class="mt-2 flex items-center text-sm text-gray-500">
              <span class="mr-2">{{ project.country?.name }}</span>
              <span>•</span>
              <span class="ml-2">{{ project.partner?.name }}</span>
            </div>
            <p class="mt-3 text-sm text-gray-600 line-clamp-3" v-html="sanitizeHtml(project.description)"></p>
            <div class="mt-4">

              <div class="mt-2 text-sm text-gray-700">
                <span class="font-medium">{{ project.plans?.length || 0 }}</span> plan{{ project.plans?.length > 1 ? 's' : '' }} disponible{{ project.plans?.length > 1 ? 's' : '' }}
              </div>
              <div class="mt-2 text-sm text-gray-700">
                À partir de <span class="font-medium">{{ getMinAmount(project.plans) }}€</span>
              </div>
              <div class="mt-4">
                <ProgressBar
                  :total="project.funding_stats?.total_plans"
                  :remaining="project.funding_stats?.remaining_plans"
                  :percentage="project.funding_stats?.funding_percentage"
                />
              </div>
            </div>
            <div class="mt-4 space-y-4">
              <div v-if="addToCartError" class="bg-red-50 border border-red-200 rounded-lg p-4">
                <p class="text-sm text-red-600">{{ addToCartError }}</p>
              </div>
              <div class="flex gap-2">
                <button
                  @click="handleViewDetails(project.id)"
                  class="btn btn-primary w-full text-center"
                >
                  Voir les détails
                </button>
                <button
                  v-if="authStore.isAuthenticated"
                  @click="handleDirectPurchase(project)"
                  class="btn btn-primary flex-grow text-center"
                  :disabled="!project.plans?.length"
                >
                  Offrir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <GiftModal
      v-if="isModalOpen"
      :is-open="isModalOpen"
      :is-processing="isProcessingGift"
      @close="closeModal"
      @submit="handleGiftSubmit"
    />

    <!-- Bulle de dialogue pour le panier -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform -translate-y-2 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform -translate-y-2 opacity-0"
    >
      <div 
        v-if="showCartBubble"
        class="fixed bottom-20 right-4 lg:bottom-8 lg:right-8 max-w-xs z-50"
      >
        <div class="relative">
          <!-- Bulle de dialogue -->
          <div class="bg-white p-4 rounded-lg shadow-xl border-2 border-zebu-orange">
            <div class="flex items-start space-x-4">
              <!-- Message -->
              <div>
                <p class="text-gray-800">
                  Pour finaliser votre cadeau, rendez-vous sur votre
                </p>
                <router-link
                  to="/cart"
                  class="inline-flex items-center mt-2 text-sm text-zebu-orange hover:text-zebu-orange-dark font-medium"
                >
                  panier
                  <svg class="ml-1 w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
          <!-- Triangle de la bulle -->
          <div class="absolute -bottom-2 right-12 w-4 h-4 transform rotate-45 bg-white border-r-2 border-b-2 border-zebu-orange"></div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { memberareaService } from '@/services/memberarea.service'
import { publicService } from '@/services/public.service.js'
import { useAuthStore } from '@/stores/auth'
import GiftModal from '@/components/modals/GiftModal.vue'
import ProgressBar from '@/components/ProgressBar.vue'
import { useToast } from 'vue-toastification'
import { sanitizeHtml } from '@/utils/sanitize'
import fallbackImage from '@/assets/fallback.jpg'

const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()
const baseUrl = import.meta.env.VITE_API_URL
const oldBaseUrl = 'https://app.zebu.net'
const loading = ref(true)
const error = ref(null)
const projects = ref([])
const isModalOpen = ref(false)
const selectedPlanId = ref(null)
const isProcessingGift = ref(false)
const addToCartError = ref(null)
const showCartBubble = ref(false)

// Fonction pour gérer les trois sources d'images possibles
const getProjectImage = (cover) => {
  if (!cover) return fallbackImage
  
  // Nettoyer le chemin de l'image (enlever le premier slash si présent)
  const cleanPath = cover.startsWith('/') ? cover.slice(1) : cover
  const filename = cleanPath.split('/').pop()
  
  // On commence toujours par l'ancien système
  return `https://app.zebu.net/uploads/projects/${filename}`
}

// Gestionnaire d'erreur pour les images avec fallback en cascade
const handleImageError = (event) => {
  const img = event.target
  const currentSrc = img.src
  const filename = currentSrc.split('/').pop()
  
  // Ordre précis de fallback
  if (currentSrc.includes('app.zebu.net')) {
    // 1. Si l'ancien système échoue, on essaie le backend local
    console.log('Ancien système échoué, tentative backend local')
    img.src = `${baseUrl}/uploads/projects/${filename}`
  } else if (currentSrc.includes(baseUrl)) {
    // 2. Si le backend local échoue, on essaie le bucket
    console.log('Backend local échoué, tentative bucket')
    img.src = `https://bucket.zebu.net/images/${filename}`
  } else if (currentSrc.includes('bucket.zebu.net')) {
    // 3. Si le bucket échoue, on utilise le fallback
    console.log('Bucket échoué, utilisation du fallback')
    img.src = fallbackImage
  }
}

const getMinAmount = (plans) => {
  if (!plans || plans.length === 0) return 0
  return Math.min(...plans.map(plan => Number(plan.amount)))
}

const handleGift = (planId) => {
  selectedPlanId.value = planId
  isModalOpen.value = true
}

const handleGiftSubmit = async (beneficiaryInfo) => {
  try {
    addToCartError.value = null;
    isProcessingGift.value = true;
    
    // Trouver le projet correspondant au plan sélectionné
    const project = projects.value.find(p => p.id === selectedPlanId.value);
    
    const response = await memberareaService.addToCart(selectedPlanId.value, true, {
      email: beneficiaryInfo.email,
      name: beneficiaryInfo.name,
      message: beneficiaryInfo.message
    });

    if (response.success) {
      // Afficher une notification toast
      toast.success('Le cadeau a été ajouté au panier', {
        timeout: 5000,
        closeButton: true
      });

      // Mise à jour des stats du projet avec les nouvelles informations
      if (response.data.availablePlans !== undefined && project) {
        project.funding_stats.remaining_plans = response.data.availablePlans;
      }
      
      isModalOpen.value = false;
      
      // Afficher la bulle de dialogue au lieu de rediriger
      showCartNotification();
    } else {
      // Gestion des réponses avec success: false
      if (response.availablePlans !== undefined && project) {
        project.funding_stats.remaining_plans = response.availablePlans;
      }
      throw new Error(response.message);
    }
  } catch (error) {
    console.error('Erreur lors de l\'ajout au panier:', error);
    let errorMessage = error.message;
    
    // Gestion des erreurs HTTP
    if (error.response?.data) {
      errorMessage = error.response.data.message;
      if (error.response.data.availablePlans !== undefined) {
        const project = projects.value.find(p => p.id === selectedPlanId.value);
        if (project) {
          project.funding_stats.remaining_plans = error.response.data.availablePlans;
        }
      }
    }
    
    addToCartError.value = errorMessage;
    toast.error(errorMessage || 'Une erreur est survenue lors de l\'ajout au panier');
  } finally {
    isProcessingGift.value = false;
  }
};

const closeModal = () => {
  isModalOpen.value = false
  selectedPlanId.value = null
}

const handleViewDetails = (projectId) => {
  // Retirer la vérification d'authentification et garder le paramètre gift=true
  router.push(`/projects/${projectId}?gift=true`)
};

const handleDirectPurchase = async (project) => {
  if (!project.plans?.length) return;
  
  // Si l'utilisateur n'est pas connecté, rediriger vers la création de compte
  if (!authStore.isAuthenticated) {
    router.push('/register')
    return
  }
  
  try {
    addToCartError.value = null;
    
    // Vérifier la disponibilité avant d'ouvrir le modal
    if (project.funding_stats?.remaining_plans <= 0) {
      addToCartError.value = "Plus aucun plan n'est disponible dans ce projet";
      toast.error(addToCartError.value);
      return;
    }

    // Vérifier si le plan est déjà dans le panier
    const cartResponse = await memberareaService.getCart();
    const projectPlansInCart = cartResponse.data?.items?.filter(item => 
      item.projectId === project.id
    )?.length || 0;

    if (projectPlansInCart >= project.funding_stats?.remaining_plans) {
      addToCartError.value = `Vous ne pouvez pas ajouter plus de plans dans le projet "${project.name}" (${project.funding_stats?.remaining_plans} disponible)`;
      toast.error(addToCartError.value);
      return;
    }
    
    selectedPlanId.value = project.id;
    isModalOpen.value = true;
  } catch (error) {
    console.error('Erreur:', error);
    addToCartError.value = error.message;
    toast.error(error.message || 'Une erreur est survenue');
  }
};

// Fonction pour afficher la bulle de dialogue du panier
const showCartNotification = () => {
  showCartBubble.value = true;
  setTimeout(() => {
    showCartBubble.value = false;
  }, 10000); // La bulle disparaît après 10 secondes
};

onMounted(async () => {
  try {
    loading.value = true
    let response
    
    if (authStore.isAuthenticated) {
      // Pour les utilisateurs connectés, utiliser l'API existante
      response = await memberareaService.getAvailableProjects()
      projects.value = response.projects || []
    } else {
      // Pour les utilisateurs non connectés, utiliser l'API publique
      response = await publicService.getAvailableProjects()
      projects.value = response.projects || []
    }
  } catch (err) {
    error.value = "Erreur lors du chargement des projets"
    console.error('Error loading projects:', err)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Mettre à jour le style pour le fallback */
img[src$="fallback.jpg"] {
  opacity: 0.5;
  background-color: #f3f4f6;
}
</style> 